import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import { ProjectCard } from "@/components/consultant/ProjectCard";
import { ProjectDetailDialog } from "@/components/consultant/ProjectDetailDialog";
import { NewProjectDialog, PROJECT_TYPES } from "@/components/consultant/NewProjectDialog";
import {
  ArrowLeft, Plus, FolderKanban, Users, Clock, CheckCircle,
  Loader2, Search, PauseCircle, Building2, LayoutGrid, UserPlus
} from "lucide-react";

export const ConsultantProjectsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterCompany, setFilterCompany] = useState("all");
  const [groupBy, setGroupBy] = useState("none");

  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [showProjectDetailDialog, setShowProjectDetailDialog] = useState(false);
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);

  const [newProjectForm, setNewProjectForm] = useState({
    client_id: "", company_id: "", title: "", project_type: "", goals: "", notes: "", milestones: []
  });
  const [newClientForm, setNewClientForm] = useState({
    name: "", email: "", phone: "", company: "", password: "Welcome2026!"
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, clientsRes, companiesRes] = await Promise.all([
        authFetch(`${API}/consultant/projects`),
        authFetch(`${API}/consultant/clients`),
        authFetch(`${API}/consultant/companies`)
      ]);
      if (projectsRes.ok) setProjects(await projectsRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (companiesRes.ok) { const d = await companiesRes.json(); setCompanies(d.companies || []); }
    } catch (_error) {
      console.error("Error fetching data:", _error);
    } finally { setLoading(false); }
  };

  const handleCreateProject = async () => {
    if (!newProjectForm.client_id || !newProjectForm.title || !newProjectForm.project_type) {
      toast.error("Please fill in required fields"); return;
    }
    setSaving(true);
    try {
      const response = await authFetch(`${API}/consultant/projects`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newProjectForm)
      });
      if (response.ok) {
        toast.success("Project created successfully!");
        setShowNewProjectDialog(false);
        setNewProjectForm({ client_id: "", company_id: "", title: "", project_type: "", goals: "", notes: "", milestones: [] });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create project");
      }
    } catch (_error) { toast.error("An error occurred"); }
    finally { setSaving(false); }
  };

  const handleCreateClientDirect = async () => {
    if (!newClientForm.name || !newClientForm.email) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      const res = await authFetch(`${API}/consultant/clients/create-direct`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newClientForm)
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Client created! Password: ${d.password}`);
        setShowAddClientDialog(false);
        setNewClientForm({ name: "", email: "", phone: "", company: "", password: "Welcome2026!" });
        fetchData();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Failed to create client");
      }
    } catch (e) { toast.error(`Error: ${e.message}`); }
    setSaving(false);
  };

  const fetchTemplates = async () => {
    try {
      const res = await authFetch(`${API}/consultant/project-templates`);
      if (res.ok) { const d = await res.json(); setTemplates(d.templates || []); }
    } catch (e) { console.error(e); }
  };

  const applyTemplate = (tmpl) => {
    setNewProjectForm(prev => ({
      ...prev, title: tmpl.name || "", project_type: tmpl.project_type || "",
      goals: tmpl.goals || "", notes: tmpl.notes || "", milestones: tmpl.milestones || []
    }));
    toast.success(`Template "${tmpl.name}" applied`);
  };

  const openProjectDetail = async (project) => {
    try {
      const response = await authFetch(`${API}/consultant/projects/${project.project_id}`);
      if (response.ok) {
        setSelectedProject(await response.json());
        setShowProjectDetailDialog(true);
      }
    } catch (_error) { toast.error("Failed to load project"); }
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = searchTerm === "" ||
      p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || p.status === filterStatus;
    const matchesType = filterType === "all" || p.project_type === filterType;
    const matchesCompany = filterCompany === "all" ||
      (filterCompany === "none" ? !p.company_id && !p.company_name : (p.company_id === filterCompany || p.company_name === filterCompany));
    return matchesSearch && matchesStatus && matchesType && matchesCompany;
  });

  const uniqueCompanies = [...new Map(
    projects.filter(p => p.company_name || p.company_id).map(p => [p.company_name || p.company_id, { id: p.company_id || p.company_name, name: p.company_name || "Unknown" }])
  ).values()];

  const groupedProjects = {};
  if (groupBy !== "none") {
    filteredProjects.forEach(p => {
      const key = groupBy === "company" ? (p.company_name || "No Company") : (p.client?.name || "Unknown Client");
      if (!groupedProjects[key]) groupedProjects[key] = [];
      groupedProjects[key].push(p);
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="consultant-projects">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/consultant")} className="text-neutral-600">
                <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-neutral-900">Project Management</h1>
                <p className="text-sm text-neutral-500">Manage client projects and track progress</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setNewClientForm({ name: "", email: "", phone: "", company: "", password: "Welcome2026!" }); setShowAddClientDialog(true); }} data-testid="add-client-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Add Client
              </Button>
              <Button onClick={() => { fetchTemplates(); setShowNewProjectDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-project-btn">
                <Plus className="w-4 h-4 mr-2" /> New Project
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="border-neutral-200"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-2xl font-bold text-neutral-900">{projects.length}</p><p className="text-sm text-neutral-500">Total Projects</p></div><FolderKanban className="w-8 h-8 text-emerald-500" /></div></CardContent></Card>
          <Card className="border-neutral-200"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-2xl font-bold text-green-600">{projects.filter(p => p.status === "active").length}</p><p className="text-sm text-neutral-500">Active</p></div><Clock className="w-8 h-8 text-green-500" /></div></CardContent></Card>
          <Card className="border-neutral-200"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-2xl font-bold text-yellow-600">{projects.filter(p => p.status === "on_hold").length}</p><p className="text-sm text-neutral-500">On Hold</p></div><PauseCircle className="w-8 h-8 text-yellow-500" /></div></CardContent></Card>
          <Card className="border-neutral-200"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-2xl font-bold text-blue-600">{projects.filter(p => p.status === "completed").length}</p><p className="text-sm text-neutral-500">Completed</p></div><CheckCircle className="w-8 h-8 text-blue-500" /></div></CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input placeholder="Search projects, clients, or companies..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="project-search-input" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="on_hold">On Hold</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40" data-testid="filter-type"><SelectValue placeholder="Project Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {PROJECT_TYPES.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
            </SelectContent>
          </Select>
          {uniqueCompanies.length > 0 && (
            <Select value={filterCompany} onValueChange={setFilterCompany}>
              <SelectTrigger className="w-44" data-testid="filter-company"><SelectValue placeholder="Company" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                <SelectItem value="none">No Company</SelectItem>
                {uniqueCompanies.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="w-40" data-testid="group-by"><SelectValue placeholder="Group By" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none"><span className="flex items-center gap-1.5"><LayoutGrid className="w-3.5 h-3.5" />No Grouping</span></SelectItem>
              <SelectItem value="company"><span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />By Company</span></SelectItem>
              <SelectItem value="client"><span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />By Client</span></SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Projects Grid */}
        {filteredProjects.length === 0 ? (
          <Card className="border-neutral-200">
            <CardContent className="p-12 text-center">
              <FolderKanban className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 mb-2">No projects found</h3>
              <p className="text-neutral-500 mb-4">Create your first project to get started</p>
              <Button onClick={() => setShowNewProjectDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" /> New Project
              </Button>
            </CardContent>
          </Card>
        ) : groupBy !== "none" ? (
          <div className="space-y-6">
            {Object.entries(groupedProjects).sort(([a], [b]) => a.localeCompare(b)).map(([groupName, groupProjects]) => (
              <div key={groupName}>
                <div className="flex items-center gap-2 mb-3">
                  {groupBy === "company" ? <Building2 className="w-4 h-4 text-blue-600" /> : <Users className="w-4 h-4 text-purple-600" />}
                  <h2 className="font-semibold text-neutral-800">{groupName}</h2>
                  <Badge variant="outline" className="text-xs">{groupProjects.length}</Badge>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupProjects.map((project) => (
                    <ProjectCard key={project.project_id} project={project} onClick={openProjectDetail} hideCompany={groupBy === "company"} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProjects.map((project) => (
              <ProjectCard key={project.project_id} project={project} onClick={openProjectDetail} />
            ))}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <NewProjectDialog
        open={showNewProjectDialog} onOpenChange={setShowNewProjectDialog}
        form={newProjectForm} setForm={setNewProjectForm}
        clients={clients} companies={companies} templates={templates}
        onApplyTemplate={applyTemplate} onSubmit={handleCreateProject} saving={saving}
      />

      <ProjectDetailDialog
        open={showProjectDetailDialog} onOpenChange={setShowProjectDetailDialog}
        project={selectedProject} setProject={setSelectedProject}
        clients={clients} companies={companies} onRefresh={fetchData}
      />

      {/* Add Client Dialog */}
      <Dialog open={showAddClientDialog} onOpenChange={setShowAddClientDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Client</DialogTitle>
            <DialogDescription>Create a client account directly (no invitation email)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input data-testid="client-name-input" value={newClientForm.name} onChange={e => setNewClientForm(p => ({...p, name: e.target.value}))} placeholder="Jane Smith" /></div>
              <div><Label>Email *</Label><Input data-testid="client-email-input" type="email" value={newClientForm.email} onChange={e => setNewClientForm(p => ({...p, email: e.target.value}))} placeholder="jane@company.com" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={newClientForm.phone} onChange={e => setNewClientForm(p => ({...p, phone: e.target.value}))} placeholder="(555) 123-4567" /></div>
              <div><Label>Company</Label><Input value={newClientForm.company} onChange={e => setNewClientForm(p => ({...p, company: e.target.value}))} placeholder="Company name" /></div>
            </div>
            <div><Label>Temporary Password</Label><Input value={newClientForm.password} onChange={e => setNewClientForm(p => ({...p, password: e.target.value}))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddClientDialog(false)}>Cancel</Button>
            <Button data-testid="create-client-submit-btn" onClick={handleCreateClientDirect} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Create Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsultantProjectsPage;
